<template>
  <b-card border-variant="primary">
    <div v-if="viewMap">
      <label class="d-block font-weight-bold mb-50">¿Dónde entregamos tu pedido?</label>
      <small class="text-muted d-block mb-1">
        Escribe tu dirección y elígela de la lista, o toca directamente el
        mapa para marcar el punto exacto.
      </small>

      <gmap-autocomplete class="introInput search-input-location" @place_changed="setPlace">
        <template v-slot:input="slotProps">
          <v-text-field
            outlined
            prepend-inner-icon="place"
            placeholder="Escribe tu dirección de entrega"
            ref="input"
            v-bind="slotProps.attrs"
            v-on="slotProps.listeners"
          >
          </v-text-field>
        </template>
      </gmap-autocomplete>

      <!-- Antes habia un boton que activaba un "modo de seleccion manual":
           habia que presionarlo y despues tocar el mapa. En celular el unico
           indicio de que el modo estaba activo era el cursor de cruz, que en
           pantallas tactiles no existe. Ahora el mapa responde siempre. -->
      <small class="text-muted d-block mb-1">
        <feather-icon icon="InfoIcon" size="14" class="mr-25" />
        ¿No quedó exacto? Toca el mapa o arrastra el marcador para ajustarlo.
      </small>

      <b-alert v-if="outOfCoverage && customerLocation.customerMarker" variant="warning" show class="p-1">
        <strong>Fuera de cobertura:</strong> esta ubicación está fuera de las zonas de domicilio de la farmacia.
        Selecciona un punto dentro del área de cobertura para poder continuar.
      </b-alert>

      <!-- Lo que el paciente necesita decidir va primero y grande: a donde
           llega, cuanto cuesta y que tan lejos esta. Las coordenadas son
           dato tecnico y quedan al final, discretas. -->
      <b-card v-if="customerLocation.selectedPlace && customerLocation.selectedPlace.geometry && typeof customerLocation.selectedPlace.geometry.location.lat === 'function'" border-variant="success">
        <b-card-body class="p-1">
          <div class="d-flex align-items-start mb-1">
            <feather-icon icon="MapPinIcon" size="20" class="mr-1 mt-25 text-success flex-shrink-0" />
            <div>
              <small class="text-muted d-block">Tu pedido llegará a</small>
              <span class="font-weight-bold" style="font-size: 1.05rem; line-height: 1.3;">
                {{ customerLocation.selectedPlace.formatted_address }}
              </span>
            </div>
          </div>

          <div class="d-flex flex-wrap gap-3 border-top pt-1">
            <div class="mr-3">
              <small class="text-muted d-block">Costo de envío</small>
              <span class="font-weight-bold text-success" style="font-size: 1.15rem;">
                {{ costoFormateado }}
              </span>
            </div>
            <div>
              <small class="text-muted d-block">Distancia</small>
              <span class="font-weight-bold" style="font-size: 1.15rem;">
                {{ customerLocation.distanceInKm }} km
              </span>
            </div>
          </div>

          <small class="text-muted d-block mt-1" style="font-size: 0.75rem;">
            Coordenadas: {{ customerLocation.selectedPlace.geometry.location.lat().toFixed(6) }},
            {{ customerLocation.selectedPlace.geometry.location.lng().toFixed(6) }}
          </small>
        </b-card-body>
      </b-card>

      <GmapMap
        :center="center"
        :zoom="13"
        style="width: 100%; height: 500px"
        :options="mapOptions"
        @click="mapClicked"
      >
        <GmapPolygon
          v-for="(polygon, index) in polygons"
          :key="index"
          :paths="polygon.paths"
          :options="{ fillColor: '#009EE0', fillOpacity: 0.25, strokeWeight: 1, clickable: false }"
        />
        <!-- Marcador de la tienda con ícono personalizado -->
        <GmapMarker v-if="center.lat && center.lng"
                    :position="center"
                    :icon="{
                      url: 'https://mimedicamento.com.co/img/logo.3a7c8281.svg',
                      scaledSize: { width: 32, height: 32 }
                    }"
        />
        <!-- Marcador de la ubicación del cliente: arrastrable para ajustar
             con precisión sin tener que buscar de nuevo. -->
        <GmapMarker v-if="customerLocation.customerMarker" :position="customerLocation.customerMarker"
                    :animation="markerAnimation" :draggable="true" @dragend="markerDragged" />

        <!-- Línea de ruta entre la tienda y el cliente -->
        <GmapPolyline v-if="customerLocation.directions"
                      :path="customerLocation.directions.routes[0].overview_path"
                      :options="{ strokeColor: '#FF0000', strokeOpacity: 1.0, strokeWeight: 3 }"
        />
      </GmapMap>
    </div>
    <div v-else>
      <b-alert variant="warning" show>
        <strong>¡Advertencia!</strong> 
        <p>La farmacia no ha registrado su ubicación.</p>
      </b-alert>
    </div>
  </b-card>
</template>

<script>
import { gmapApi } from 'vue2-google-maps';
import { mapState, mapMutations, mapActions } from "@/store/deliveries";

export default {
  data() {
    return {
      viewMap : true,
      center: { lat: 0, lng: 0 },
      polygons: [],
      address: '',
      geocoder: null,
      markerAnimation: null,
      success: false,
      error: false,
      googleLoaded: false,
      outOfCoverage: false,
      autoLocated: false,
      mapOptions: {
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
        disableDefaultUi: false,
      },
    };
  },
  computed: {
    ...mapState(["customerLocation", "customer"]),
    google() {
      return gmapApi() || null;
    },
    // Antes se mostraba "$ 3500". Con separador de miles se lee de un
    // vistazo, que es lo que importa cuando decide si continuar.
    costoFormateado() {
      const costo = Number(this.customerLocation.calculatedCost);

      if (!isFinite(costo)) return '—';
      if (costo === 0) return 'Gratis';

      return '$ ' + costo.toLocaleString('es-CO');
    }
  },
  created() {
    this.getCustomerLocation();
    this.getCustomerCoverages();
  },
  mounted() {
    if (this.google && this.google.maps) {
      this.initializeGoogleMapsData();
    } else {
      this.$watch(
        () => this.google,
        (google) => {
          if (google && google.maps) {
            this.initializeGoogleMapsData();
          }
        }
      );
    }
  },
  watch: {
    viewMap(newValue) {
      console.log('viewMap ha cambiado:', newValue);
    },
  },
  methods: {
    ...mapMutations(["setCustomerLocation"]),
    getCustomerLocation() {
      this.$http.get('/customers/get-location/' + this.customer.id, this.formData)
        .then((response) => {
          const viewMapState = !!response.data.customerLocation.location;
          // Convertir a booleano
          console.log('viewMapState:', viewMapState);
          this.$set(this, 'viewMap', viewMapState);
          if (viewMapState) {
            this.center = response.data.customerLocation.location;
            this.tryAutoLocate(); // Ubicar al cliente por defecto (si acepta el permiso)
          }
          this.emitReadiness(); // Aún no hay ubicación válida seleccionada
        })
        .catch((error) => {
          console.error('Error al obtener la ubicación del cliente:', error);
          this.$set(this, 'viewMap', false);
          this.emitReadiness();
        });
    },

    initializeGoogleMapsData() {
      if (this.customerLocation.selectedPlace && this.customerLocation.selectedPlace.geometry) {
        // Verifica que lat y lng sean funciones
        if (typeof this.customerLocation.selectedPlace.geometry.location.lat !== 'function') {
          const location = this.customerLocation.selectedPlace.geometry.location;
          this.customerLocation.selectedPlace.geometry.location = new this.google.maps.LatLng(location.lat, location.lng);
        }
      }

      this.googleLoaded = true;
      this.geocoder = new this.google.maps.Geocoder();
      this.tryAutoLocate(); // Por si el mapa cargó después de obtener la ubicación de la farmacia
    },

    // Ubica al cliente automáticamente usando el GPS del navegador (una sola vez).
    tryAutoLocate() {
      if (this.autoLocated || !this.viewMap || !this.geocoder || !navigator.geolocation) return;
      this.autoLocated = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const location = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          this.geocoder.geocode({ location }, (results, status) => {
            const place = (status === 'OK' && results[0])
              ? results[0]
              : { formatted_address: 'Mi ubicación actual', geometry: { location: new this.google.maps.LatLng(location.lat, location.lng) } };
            this.handleSelected(location, place);
          });
        },
        () => { /* el usuario negó el permiso: puede seleccionar manualmente */ },
        { enableHighAccuracy: true, timeout: 8000 }
      );
    },

    // Informa al wizard si se puede continuar: requiere una ubicación
    // seleccionada que esté DENTRO de alguna zona de cobertura.
    emitReadiness() {
      const ready = this.viewMap && !!this.customerLocation.customerMarker && !this.outOfCoverage;
      this.$emit('update-view-map', ready);
      // Un boton deshabilitado sin explicacion desconcierta, sobre todo en
      // celular, donde el aviso queda lejos del boton. Se manda el motivo
      // para mostrarlo junto a el.
      this.$emit('update-block-reason', ready ? null : this.motivoBloqueo());
    },

    motivoBloqueo() {
      if (!this.viewMap) {
        return 'Esta farmacia no ha registrado su ubicación, por eso no podemos calcular el domicilio.';
      }

      if (!this.customerLocation.customerMarker) {
        return 'Indica tu dirección de entrega en el mapa para continuar.';
      }

      if (this.outOfCoverage) {
        return 'Tu ubicación queda fuera de las zonas de domicilio de esta farmacia. Elige un punto dentro del área sombreada para continuar.';
      }

      return null;
    },

    // Determina la zona que contiene el punto y calcula ruta + costo.
    handleSelected(location, place) {
      const zone = this.getPolygonDataForCoordinate(location);
      this.calculateRoute(location, place, zone);
    },

    getCustomerCoverages() {
      this.$http.get('/customers/get-coverages/' + this.customer.id, this.formData)
        .then((response) => {
          this.polygons = response.data.customerCoverages;
        });
    },

    setPlace(place) {
      if (!place.geometry) {
        console.log("No details available for input: '" + place.name + "'");
        return;
      }

      const location = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      };

      this.handleSelected(location, place);
    },
    getPolygonDataForCoordinate(coordinate) {
      if (!this.googleLoaded) return null;

      const google = this.google;
      for (const polygon of this.polygons) {
        const gPolygon = new google.maps.Polygon({ paths: polygon.paths });
        const latLng = new google.maps.LatLng(coordinate.lat, coordinate.lng);
        if (google.maps.geometry.poly.containsLocation(latLng, gPolygon)) {
          return polygon;
        }
      }
      return null;
    },
    async searchAddress() {
      const result = await this.geocodeAddress(this.address);
      const location = result[0].geometry.location;
      this.handleSelected({ lat: location.lat(), lng: location.lng() }, result[0]);
    },
    geocodeAddress(address) {
      return new Promise((resolve, reject) => {
        this.geocoder.geocode({ address }, (results, status) => {
          if (status === 'OK') {
            resolve(results);
          } else {
            reject(new Error('Geocode was not successful for the following reason: ' + status));
          }
        });
      });
    },
    // Tocar el mapa marca la ubicación directamente. Arrastrar el mapa no
    // dispara este evento, así que no hay riesgo de mover el pin sin querer.
    mapClicked(event) {
      this.setPointLocation({
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      });
    },

    // Ajuste fino: soltar el marcador en otro punto.
    markerDragged(event) {
      this.setPointLocation({
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      });
    },

    setPointLocation(location) {
      if (!this.geocoder) return;

      // Geocodificar la ubicación seleccionada manualmente
      this.geocoder.geocode({ location }, (results, status) => {
        if (status === 'OK' && results[0]) {
          this.handleSelected(location, results[0]);
        } else {
          this.success = false;
          this.error = true;
          this.outOfCoverage = false;
          this.setCustomerLocation({
            customerMarker: null,
            distanceInKm: null,
            calculatedCost: null,
            directions: null,
            selectedPlace: null,
          });
          this.emitReadiness();
        }
      });

      // Animar el marcador
      this.markerAnimation = this.google.maps.Animation.BOUNCE;
      setTimeout(() => {
        this.markerAnimation = null;
      }, 750);
    },
    calculateRoute(location, place, zone) {
      const directionsService = new this.google.maps.DirectionsService();

      directionsService.route(
        {
          origin: this.center,
          destination: location,
          travelMode: this.google.maps.TravelMode.DRIVING, // Puedes cambiar a BICYCLING, WALKING, etc.
        },
        (response, status) => {
          if (status === this.google.maps.DirectionsStatus.OK) {
            const route = response.routes[0];
            let totalDistance = 0;
            route.legs.forEach((leg) => {
              totalDistance += leg.distance.value; // Distancia en metros
            });

            const distanceInKm = (totalDistance / 1000).toFixed(2); // Convertir a kilómetros y redondear

            // El costo depende del tipo de la zona que contiene el punto.
            let deliveryCost = 0;
            if (zone) {
              this.outOfCoverage = false;
              if (zone.pricingType === 'distance') {
                deliveryCost = Math.round(parseFloat(distanceInKm) * Number(zone.pricePerKm || 0));
              } else {
                deliveryCost = Number(zone.deliveryCost || 0);
              }
            } else {
              // El punto está fuera de todas las zonas de cobertura de la farmacia.
              this.outOfCoverage = true;
              deliveryCost = 0;
            }

            // Actualizar el estado en el store de Vuex
            this.setCustomerLocation({
              customerMarker: location,
              distanceInKm: distanceInKm,
              calculatedCost: deliveryCost,
              directions: response,
              selectedPlace: place,
            });
            this.emitReadiness(); // habilita/inhabilita "Siguiente" según la cobertura
          } else {
            console.error('Error fetching directions: ' + status);
          }
        }
      );
    },
  },
};
</script>

<style scoped>
.search-input-location {
  width: 100%;
  margin-bottom: 10px;
  padding: 10px;
  border: 1px solid #009EE0 !important;
  border-radius: 0.300rem;
}

@media (max-width: 768px) {
  .font-wizard {
    font-size: 10px;
  }
}
</style>
      